'use client';

import { memo } from 'react';
import { Box, Typography, Stack, Avatar, Button } from '@mui/material';
import { Phone } from '@mui/icons-material';
import { useTranslation } from '@/kit/useTranslation';

/**
 * Rider information section - supports single and multi-leg display
 */
const RiderSection = memo(({ riderName, riderPhone, statusColor, isMultiStage, legs = [] }) => {
  const { t } = useTranslation();
  const hasRiderInfo =
    riderName || (legs.length > 0 && legs.some((leg) => leg.parcelLegsRiderName));

  if (!hasRiderInfo) return null;

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {isMultiStage && legs.length > 1
          ? t('bookingDetails.assignedRidersMultiLeg')
          : t('bookingDetails.riderInformation')}
      </Typography>

      {isMultiStage && legs.length > 1 ? (
        <Stack spacing={1.5}>
          {legs.map(
            (leg, index) =>
              leg.parcelLegsRiderName && (
                <Stack key={index} direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: statusColor,
                      fontWeight: 700,
                      fontSize: 14,
                    }}
                  >
                    {leg.parcelLegsRiderName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={700}>
                      {t('bookingDetails.legRider', {
                        leg: leg.parcelLegsLegSequence || index + 1,
                        name: leg.parcelLegsRiderName,
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('bookingDetails.riderId', {
                        id: leg.parcelLegsRiderId || t('bookingDetails.na'),
                      })}
                      {leg.parcelLegsRiderPhone && ` • ${leg.parcelLegsRiderPhone}`}
                    </Typography>
                  </Box>
                  {leg.parcelLegsRiderPhone && (
                    <Button
                      href={`tel:${leg.parcelLegsRiderPhone}`}
                      variant="outlined"
                      size="small"
                      startIcon={<Phone />}
                      sx={{
                        borderColor: statusColor,
                        color: statusColor,
                        '&:hover': {
                          borderColor: statusColor,
                          bgcolor: `${statusColor}10`,
                        },
                      }}
                    >
                      {t('bookingDetails.call')}
                    </Button>
                  )}
                </Stack>
              )
          )}
        </Stack>
      ) : riderName ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: statusColor,
              fontWeight: 700,
            }}
          >
            {riderName.charAt(0).toUpperCase()}
          </Avatar>
          <Box flex={1}>
            <Typography variant="body2" fontWeight={700}>
              {riderName}
            </Typography>
            {riderPhone && (
              <Typography variant="caption" color="text.secondary">
                {riderPhone}
              </Typography>
            )}
          </Box>
          {riderPhone && (
            <Button
              href={`tel:${riderPhone}`}
              variant="contained"
              size="small"
              startIcon={<Phone />}
              sx={{
                bgcolor: statusColor,
                '&:hover': {
                  bgcolor: statusColor,
                  opacity: 0.9,
                },
              }}
            >
              {t('bookingDetails.call')}
            </Button>
          )}
        </Stack>
      ) : null}
    </Box>
  );
});

RiderSection.displayName = 'RiderSection';

export default RiderSection;
